import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BACKEND_URL } from '../utils/config';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Trophy,
  Medal,
  Users,
  ChevronRight,
  Loader2,
  ArrowLeft,
  TrendingUp,
  Heart,
} from 'lucide-react';

const TeamLeaderboardPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [campaignRes, leaderboardRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/campaigns/${campaignId}`),
          fetch(`${BACKEND_URL}/api/team-pages/campaign/${campaignId}/leaderboard`)
        ]);
        
        if (campaignRes.ok) {
          setCampaign(await campaignRes.json());
        } else {
          throw new Error('Campaign not found');
        }
        
        if (leaderboardRes.ok) {
          const data = await leaderboardRes.json();
          setLeaderboard(data.leaderboard || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [campaignId]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-muted-foreground font-medium">{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/30';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-orange-500/20 border-amber-600/30';
      default:
        return 'bg-card border-border';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">Campaign Not Found</h2>
            <Link to="/discover">
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                Discover Campaigns
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-card border-b border-border">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center text-sm text-muted-foreground">
              <Link to="/" className="hover:text-emerald-400">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to={`/campaign/${campaignId}`} className="hover:text-emerald-400">
                {campaign.title}
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-foreground">Leaderboard</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Back Link */}
          <Link to={`/campaign/${campaignId}`} className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to campaign
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Team Fundraising Leaderboard</h1>
            <p className="text-muted-foreground">
              Top fundraisers for "{campaign.title}"
            </p>
          </div>

          {/* Campaign Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-foreground">{leaderboard.length}</p>
              <p className="text-sm text-muted-foreground">Fundraisers</p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-emerald-400">
                {formatCurrency(leaderboard.reduce((sum, p) => sum + (p.amount_raised || 0), 0))}
              </p>
              <p className="text-sm text-muted-foreground">Team Raised</p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-foreground">
                {leaderboard.reduce((sum, p) => sum + (p.donor_count || 0), 0)}
              </p>
              <p className="text-sm text-muted-foreground">Donors</p>
            </div>
          </div>

          {/* Join CTA */}
          <div className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-xl p-5 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-foreground mb-1">Want to join the leaderboard?</h3>
                <p className="text-sm text-muted-foreground">Create your own fundraising page and start raising!</p>
              </div>
              {campaign.team_fundraising_enabled && (
                <Link to={`/campaign/${campaignId}/fundraise`}>
                  <Button className="bg-violet-500 hover:bg-violet-600">
                    <Users className="w-4 h-4 mr-2" /> Start Fundraising
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Leaderboard */}
          {leaderboard.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Fundraisers Yet</h3>
              <p className="text-muted-foreground">Be the first to start fundraising!</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="team-leaderboard">
              {leaderboard.map((page, index) => (
                <Link
                  key={page.id}
                  to={`/fundraise/${page.slug}`}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:scale-[1.01] ${getRankBg(index + 1)}`}
                >
                  {/* Rank */}
                  <div className="w-10 flex justify-center">
                    {getRankIcon(index + 1)}
                  </div>

                  {/* Avatar & Info */}
                  <img
                    src={page.owner_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(page.owner_name)}`}
                    alt={page.owner_name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground line-clamp-1">{page.title}</h4>
                    <p className="text-sm text-muted-foreground">{page.owner_name}</p>
                  </div>

                  {/* Stats */}
                  <div className="text-right">
                    <p className="font-bold text-emerald-400">{formatCurrency(page.amount_raised)}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                      <Heart className="w-3 h-3" /> {page.donor_count || 0} donors
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TeamLeaderboardPage;
